import React, { useContext, useState } from "react";
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    height: "calc(100% - 78px)",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    padding: "12px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
    margin: "4px 0",
    borderRadius: "12px",
    transition: "all 0.3s ease",
    padding: "12px 16px",
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' 
        ? "hsla(270, 80%, 60%, 0.05)" 
        : "hsla(270, 80%, 60%, 0.1)",
      transform: "translateX(4px)",
    },
  },
  listItemActive: {
    backgroundColor: theme.palette.type === 'light' 
      ? "hsla(270, 80%, 60%, 0.08)" 
      : "hsla(270, 80%, 60%, 0.2)",
    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: "700",
    },
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' 
        ? "hsla(270, 80%, 100%, 0.12)" 
        : "hsla(270, 80%, 100%, 0.25)",
    },
  },
  primaryText: {
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  secondaryText: {
    fontSize: "12px",
    opacity: 0.7,
    marginTop: "4px",
  },
  unreadBadge: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    height: "20px",
    minWidth: "20px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
  },
}));

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  const { id } = useParams();

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }

    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
      handleSelectChat(chat);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users.find((u) => u.userId === user.id);
    return currentUser.unreads;
  };

  const getPrimaryText = (chat) => {
    const unreads = unreadMessages(chat);
    return (
      <div className={classes.primaryText}>
        {chat.title}
        {unreads > 0 && (
          <div className={classes.unreadBadge}>
            {unreads}
          </div>
        )}
      </div>
    );
  };

  const getSecondaryText = (chat) => {
    return datetimeToClient(chat.updatedAt); // Exibe apenas a data e horário da última atualização
  };

  return (
    <>
      <ConfirmationModal
        title={"Excluir Conversa"}
        open={confirmationModal}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser revertida, confirmar?
      </ConfirmationModal>
      <div className={classes.mainContainer}>
        <div className={classes.chatList}>
          <List>
            {Array.isArray(chats) &&
              chats.length > 0 &&
              chats.map((chat, key) => (
                <ListItem
                  onClick={() => goToMessages(chat)}
                  key={key}
                  className={`${classes.listItem} ${chat.uuid === id ? classes.listItemActive : ""}`}
                  button
                >
                  <ListItemText
                    primary={getPrimaryText(chat)}
                    secondary={
                      <span className={classes.secondaryText}>
                        {getSecondaryText(chat)}
                      </span>
                    }
                  />
                  {chat.ownerId === user.id && (
<ListItemSecondaryAction style={{ display: "flex", gap: "10px" }}>
  <IconButton
    onClick={() => {
      goToMessages(chat).then(() => {
        handleEditChat(chat);
      });
    }}
    size="small"
    style={{
      backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.15)" : "#e3f2fd",
      padding: "8px",
      borderRadius: "10px",
      color: "#1976d2"
    }}
    title="Editar Conversa"
  >
    <EditIcon />
  </IconButton>

  <IconButton
    onClick={() => {
      setSelectedChat(chat);
      setConfirmModalOpen(true);
    }}
    size="small"
    style={{
      backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.15)" : "#ffebee",
      padding: "8px",
      borderRadius: "10px",
      color: "#d32f2f"
    }}
    title="Excluir Conversa"
  >
    <DeleteIcon />
  </IconButton>
</ListItemSecondaryAction>

                  )}
                </ListItem>
              ))}
          </List>
        </div>
      </div>
    </>
  );
}